import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
//
import { Select } from 'antd';
import { CaretDown } from 'phosphor-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CarFilterType, CarSortType } from '../../interface/car';
import {
    resetBuyCarsList,
    setBuyCarListPage
} from '../../redux/actions/carListAction';
import { getCarData } from '../../redux/actions/carListingAction';
import {
    getCarAvailableBrand,
    getCarAvailablsModels
} from '../../redux/actions/carModelsAction';
import { ICombineReducerType } from '../../redux/reducers';
import { Stringfy } from '../../utils/Helper/jsHelpers';
import { decodeCompactNumber } from '../../utils/Helper/PriceFormatter';
import CarCard from '../CarCard';
import Loading from '../ui/Loading';
import { NoCarFound } from '../ui/NoCarFound';
import WhatsAppSimpleBanner from '../WhatsAppSimpleBanner';

// TODO confirm this value
const SortByArray = [
    { name: 'recentlyAdded', value: '-_id' },
    { name: 'highestPriceFirst', value: '-price' },
    { name: 'lowestPriceFirst', value: 'price' },
    { name: 'highestMileageFirst', value: '-mileage' },
    { name: 'lowestMileageFirst', value: 'mileage' }
];

const CarListSection = ({
    activeBrand,
    activeModel,
    activeBodyType,
    priceRange,
    minPriceFromURL,
    maxPriceFromURL,
    voetstoots
}: {
    activeBrand?: string;
    activeModel?: string;
    activeBodyType?: string;
    priceRange?: string;
    minPriceFromURL?: string;
    maxPriceFromURL?: string;
    voetstoots?: boolean;
}) => {
    const { t } = useTranslation();
    const [is_loading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const carListData = useSelector(
        (state: ICombineReducerType) => state.carListReducer
    );
    const observerRef = useRef<IntersectionObserver>();
    const [has_more, setHasMore] = useState(false); // Initialize as true to allow first page fetch
    const dispatch = useDispatch();

    const onSortSelect = (sort_value: string) => {
        setSearchParams((prevSearchParam) => {
            const newParams = new URLSearchParams(prevSearchParam);
            newParams.set('sort', sort_value);
            return newParams;
        });
    };

    /**
     *
     * @param reset_page Determine if the data fetched is from changing filter or scrolling to end
     * In case when fetching data from filter we don't increment the page count, but want to reset it to 1
     * And In case when fetching data from scrolling to the end we want to increment the page count so we can use that increment value next time
     */
    const fetchData = async (reset_page: boolean) => {
        try {
            setIsLoading(true);
            const carBrands = searchParams.getAll('brand');
            if (carBrands.length === 0) {
                if (!activeBrand) {
                    getCarAvailablsModels(dispatch);
                }
            } else {
                carBrands.forEach((brand) => {
                    getCarAvailableBrand(dispatch, brand);
                });

                if (activeBrand) {
                    getCarAvailableBrand(dispatch, activeBrand);
                }
            }

            if (reset_page) {
                await resetBuyCarsList(dispatch);
            }
            const resData = await getCarData(dispatch, {
                search: searchParams.get('search') ?? undefined,
                brands: activeBrand
                    ? Stringfy([activeBrand])
                    : Stringfy(searchParams.getAll('brand')) ?? undefined,
                models: activeModel
                    ? Stringfy([activeModel])
                    : Stringfy(searchParams.getAll('model')) ?? undefined,
                minPrice: minPriceFromURL
                    ? minPriceFromURL
                    : searchParams.get('minPrice') ?? undefined,
                maxPrice: maxPriceFromURL
                    ? maxPriceFromURL
                    : priceRange
                    ? decodeCompactNumber(priceRange)
                    : searchParams.get('maxPrice') ?? undefined,
                colors: Stringfy(searchParams.getAll('color')) ?? undefined,
                transmissions:
                    Stringfy(searchParams.getAll('transmission')) ?? undefined,
                bodyTypes: activeBodyType
                    ? Stringfy([activeBodyType])
                    : Stringfy(searchParams.getAll('bodyType')) ?? undefined,
                fuelType: searchParams.get('fuelType') ?? undefined,
                sort: (searchParams.get('sort') as CarSortType) ?? '-price',
                isNewCar:
                    (searchParams.get('isNewCar') as CarFilterType) ??
                    undefined,
                isElectric:
                    (searchParams.get('isElectric') as CarFilterType) ??
                    undefined,
                imported:
                    (searchParams.get('imported') as CarFilterType) ??
                    undefined,
                minMileage: searchParams.get('minMileage') ?? undefined,
                maxMileage: searchParams.get('maxMileage') ?? undefined,
                minMonthlyInstallment:
                    searchParams.get('minMonthlyInstallment') ?? undefined,
                maxMonthlyInstallment:
                    searchParams.get('maxMonthlyInstallment') ?? undefined,
                discount:
                    (searchParams.get('discount') as CarFilterType) ??
                    undefined,
                minModelYear: searchParams.get('minModelYear') ?? undefined,
                maxModelYear: searchParams.get('maxModelYear') ?? undefined,
                page: reset_page ? 1 : carListData?.buyPage,
                limit: 16,
                incoming: searchParams.get('incoming') ?? undefined,
                voetstoots: voetstoots ?? undefined
            });
            if (resData.status === 200) {
                const currentPage = reset_page ? 1 : carListData?.buyPage;
                await setBuyCarListPage(
                    dispatch,
                    reset_page ? 2 : carListData?.buyPage + 1
                );
                // Use the current page that was requested to check if there are more pages
                const hasMorePages = currentPage < resData?.data?.totalPages;
                setHasMore(hasMorePages);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.trace(error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetch = useDebouncedCallback(fetchData, 1000);

    const lastItemRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (is_loading) return;
            if (observerRef.current) observerRef.current.disconnect();
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        if (!is_loading && has_more) {
                            setIsLoading(true);
                            debouncedFetch(false);
                            return;
                        }
                        setHasMore(false);
                    }
                },
                {
                    rootMargin: '20px'
                }
            );
            if (node) observerRef.current.observe(node);
        },
        [
            is_loading,
            has_more,
            debouncedFetch,
            carListData?.buyPage,
            carListData?.totalBuyPage
        ]
    );

    useEffect(() => {
        fetchData(true);
    }, [
        searchParams,
        activeBrand,
        activeModel,
        activeBodyType,
        priceRange,
        minPriceFromURL,
        maxPriceFromURL
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeModel, activeBrand]);

    // Initialize has_more state when carListData changes
    useEffect(() => {
        if (carListData?.totalBuyPage && carListData?.totalBuyPage > 0) {
            const hasMorePages = carListData.buyPage < carListData.totalBuyPage;
            setHasMore(hasMorePages);
        }
    }, [carListData?.buyPage, carListData?.totalBuyPage]);

    return (
        <section>
            <div className="car-list-section">
                <div className="padding">
                    <div className="car-list-header">
                        <h3 className="car-list-header-title">
                            {`${t('xlistingPageResultTitle', {
                                number: carListData?.totalBuyCars
                            })}`}
                        </h3>
                        <div className="car-list-header-sort">
                            <span className="sort-title">{t('sortBy')}</span>
                            <Select
                                defaultValue={
                                    searchParams.get('sort') ??
                                    SortByArray[0].value
                                }
                                className="car-list-sort-select"
                                defaultActiveFirstOption={true}
                                bordered={false}
                                getPopupContainer={(trigger) =>
                                    trigger.parentNode
                                }
                                onChange={onSortSelect}
                                suffixIcon={
                                    <CaretDown
                                        size={20}
                                        color="#175adc"
                                        weight="bold"
                                    />
                                }
                            >
                                {SortByArray.map((options, index) => {
                                    return (
                                        <Select.Option
                                            value={options.value}
                                            key={index}
                                            dropdownClassName="sortby-dropdown"
                                        >
                                            {t(options.name)}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                    {carListData?.buyCars &&
                        carListData?.buyCars?.length === 0 &&
                        !is_loading && <NoCarFound />}
                    <div className="car-list-grid">
                        {carListData?.buyCars?.map((el, index) => (
                            <>
                                <div
                                    key={`${el._id}-${index}`}
                                    ref={
                                        index ===
                                        carListData?.buyCars?.length - 1
                                            ? lastItemRef
                                            : null
                                    }
                                >
                                    <CarCard
                                        data={el}
                                        newCarFlag={el.isNewCar}
                                    />
                                </div>
                                {(carListData?.buyCars?.length < 8
                                    ? index === carListData?.buyCars?.length - 1
                                    : index === 7) && (
                                    <div
                                        className="whatsapp-banner-full-width"
                                        style={{
                                            gridColumn: '1 / -1',
                                            width: '100%'
                                        }}
                                    >
                                        <WhatsAppSimpleBanner />
                                    </div>
                                )}
                            </>
                        ))}
                    </div>

                    {is_loading ? (
                        <Loading
                            style={{
                                display: 'block'
                            }}
                            id="lower"
                        />
                    ) : (
                        <></>
                    )}
                </div>
            </div>
        </section>
    );
};

export default CarListSection;
